import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BarChart2, TrendingUp, Users, Film, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const GENRE_COLORS = ["#D4AF37", "#7A1921", "#8FBC8F", "#EE9CA1", "#EEDC82", "#4A9FE0", "#575148", "#8B7328", "#F5F0E6", "#A8A398"];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/analytics/overview?days=${days}`);
      setData(data);
    } catch (e) { console.error(formatApiErrorDetail(e.response?.data?.detail) || "Analytics fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (!data) return <div className="text-[#A8A398]">Lade Analytics …</div>;
  const { top_movies = [], revenue_series = [], genre_split = [], returning_rate = {}, totals = {} } = data;
  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Analytics</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><BarChart2 size={30} className="text-[#D4AF37]" /> Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
              className={`text-[10px] uppercase tracking-widest px-3 py-2 rounded border ${days === d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
              {d === 365 ? "1 Jahr" : `${d} Tage`}
            </button>
          ))}
          <button onClick={load} disabled={busy} className="text-[10px] uppercase tracking-widest px-3 py-2 rounded border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="analytics-refresh">
            <RefreshCw size={12} className={`inline ${busy ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Tickets verkauft" value={totals.tickets_sold ?? 0} icon={<Users size={20} className="text-[#D4AF37]" />} />
        <Kpi label="Ticket-Umsatz" value={`${(totals.ticket_revenue ?? 0).toFixed(2)} €`} icon={<TrendingUp size={20} className="text-[#8FBC8F]" />} />
        <Kpi label="Snack-Umsatz" value={`${(totals.snack_revenue ?? 0).toFixed(2)} €`} icon={<TrendingUp size={20} className="text-[#EEDC82]" />} />
        <Kpi label={`Wiederkehrer · ${returning_rate.rate_pct ?? 0}%`} value={`${returning_rate.returning ?? 0} / ${returning_rate.total_guests ?? 0}`} icon={<RefreshCw size={20} className="text-[#4A9FE0]" />} />
      </div>

      {/* Umsatz-Verlauf */}
      <div className="card-lux p-4">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3 flex items-center gap-2"><TrendingUp size={14} /> Umsatz pro Tag</div>
        {revenue_series.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={revenue_series}>
              <CartesianGrid stroke="#1A1A1A" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#575148" fontSize={10} tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#575148" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A", fontSize: 11 }} />
              <Line type="monotone" dataKey="ticket_revenue" stroke="#D4AF37" strokeWidth={2} dot={false} name="Ticket" />
              <Line type="monotone" dataKey="snack_revenue" stroke="#EEDC82" strokeWidth={2} dot={false} name="Snack" />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="text-[#575148] text-xs py-10 text-center">Keine Daten im Zeitraum</div>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Auslastung pro Film */}
        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3 flex items-center gap-2"><Film size={14} /> Auslastung pro Film</div>
          {top_movies.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={top_movies} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid stroke="#1A1A1A" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#575148" fontSize={10} unit="%" />
                <YAxis dataKey="movie" type="category" stroke="#F5F0E6" fontSize={10} width={130} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2A2A2A", fontSize: 11 }} formatter={(v) => `${v}%`} />
                <Bar dataKey="occupancy" fill="#D4AF37" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-[#575148] text-xs py-10 text-center">Noch keine Vorstellungen</div>}
        </div>

        {/* Genre-Split */}
        <div className="card-lux p-4">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Genre-Verteilung</div>
          {genre_split.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={genre_split} dataKey="count" nameKey="genre" cx="50%" cy="50%" outerRadius={90} label={(x) => `${x.genre} (${x.count})`} labelLine={false}>
                  {genre_split.map((_, i) => <Cell key={i} fill={GENRE_COLORS[i % GENRE_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10, color: "#A8A398" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-[#575148] text-xs py-10 text-center">Keine Genre-Daten</div>}
        </div>
      </div>

      <div className="text-[10px] text-[#575148]">Zeitraum: letzte {data.range_days} Tage · ab {data.from}</div>
    </div>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div className="card-lux p-4 flex items-center gap-3" data-testid={`analytics-kpi-${(label || "").toLowerCase().split(" ")[0]}`}>
      <div className="w-12 h-12 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] flex items-center justify-center">{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</div>
        <div className="font-heading text-2xl text-[#EEDC82]">{value}</div>
      </div>
    </div>
  );
}
